import React, { useState, useEffect, useRef } from "react";
import {
  Box,
  TextField,
  Select,
  MenuItem,
  Button,
  FormHelperText,
  FormControl,
  Typography,
  InputLabel,
} from "@mui/material";
import {
  DirectorsApi,
  getDirectorsApi,
  editDirectorsApi,
} from "../../apiServices/services/OfficeServices";
import { searchPersonDetails } from "../../apiServices/services/CheckoutServices";
import ReactDatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import { format, parse, isValid } from "date-fns";
import { updateFormStatus } from "../../apiServices/services/ProfileServices";
import { countries } from "../../constants/countries";
import InputMask from "react-input-mask";
import nationalityList from "./nationalityList";
import countryResidenceList from "./countryResidenceList";

const dateFormatRegex = /^\d{2}\/\d{2}\/\d{4}$/;

function getAge(date) {
  const today = new Date();
  let age = today.getFullYear() - date.getFullYear();
  const m = today.getMonth() - date.getMonth();
  if (m < 0 || (m === 0 && today.getDate() < date.getDate())) {
    age--;
  }
  return age;
}

const DateMaskedInput = React.forwardRef(function DateMaskedInput(props, ref) {
  const { error, helperText, ...other } = props;
  return (
    <InputMask mask="99/99/9999" maskChar="" {...other} ref={ref}>
      {(inputProps) => (
        <TextField
          {...inputProps}
          error={error}
          helperText={helperText}
          fullWidth
        />
      )}
    </InputMask>
  );
});

const Directors = ({ onContinue, onBack }) => {
  const [formData, setFormData] = useState({
    shareHolders: [
      {
        position: "",
        firstName: "",
        middleName: "",
        surName: "",
        nationality: "",
        countryOfResidence: "",
        email: "",
        mobileNumber: "",
        addressLine1: "",
        addressLine2: "",
        town: "",
        region: "",
        postCode: "",
        country: "",
        dateOfBirth: "",
        firstIdentificationType: "",
        firstIdentificationAnswer: "",
        secondIdentificationType: "",
        secondIdentificationAnswer: "",
        thirdIdentificationType: "",
        thirdIdentificationAnswer: "",
        noOfShare: "",
        shareCurrency: "GBP",
        shareClass: "Ordinary",
        amountPaidDuePerShare: "0",
        amountUnpaidPerShare: "1",
        isPsc: false,
        natureOfControl: "",
      },
    ],
    userId: localStorage.getItem("userId"),
    isFormCompleted: false,
  });

  const [userId, setUserId] = useState(0);
  const [counntries, setCountries] = useState(countries);
  const [personKnown, setPersonKnown] = useState("no");
  const [errors, setErrors] = React.useState({});
  const consentType = [
    "Mother's Maiden name",
    "Father's first name",
    "National Insurance Number",
    "Telephone Number",
    " Town of birth",
    "Passport number",
  ];

  const shareHoldingRef = useRef(null);

  const [dateInputs, setDateInputs] = useState(
    formData.shareHolders.map((sh) =>
      sh.dateOfBirth ? format(new Date(sh.dateOfBirth), "dd/MM/yyyy") : ""
    )
  );
  const [dateErrors, setDateErrors] = useState(
    formData.shareHolders.map(() => "")
  );

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  useEffect(() => {
    setDateInputs(
      (formData.shareHolders || []).map((sh) =>
        sh.dateOfBirth ? format(new Date(sh.dateOfBirth), "dd/MM/yyyy") : ""
      )
    );
    setDateErrors((formData.shareHolders || []).map(() => ""));
  }, [formData.shareHolders.length]);

  const handleInputChange = (index, e) => {
    const { name, value } = e.target;

    if (name === "mobileNumber" && value.length > 13) {
      return;
    } else {
      setFormData((prevFormData) => {
        const updatedShareHolders = [...prevFormData.shareHolders];
        updatedShareHolders[index] = {
          ...updatedShareHolders[index],
          [name]: value,
        };

        return { ...prevFormData, shareHolders: updatedShareHolders };
      });
    }
  };

  useEffect(() => {
    const getcountriess = async () => {
      // Check if counntries is an array or object
      const allCountries = Array.isArray(counntries)
        ? counntries
        : Object.values(counntries || {});

      // Extract { name, code } objects and filter out any incomplete entries
      const countryList = allCountries
        .map((country) => ({
          name: country?.name?.common,
          code: country?.cca3,
        }))
        .filter((c) => c.name && c.code);

      // Sort alphabetically by name
      const sortedCountries = [...countryList].sort((a, b) =>
        a.name.localeCompare(b.name)
      );

      // Move United Kingdom to the top if it exists
      const ukIndex = sortedCountries.findIndex(
        (country) => country.name === "United Kingdom"
      );
      if (ukIndex !== -1) {
        const [uk] = sortedCountries.splice(ukIndex, 1);
        sortedCountries.unshift(uk);
      }

      setCountries(sortedCountries);
    };

    getcountriess();
    getDirectorsApi(formData.userId).then((response) => {
      if (response?.attributes?.results[0]) {
        setUserId(response?.attributes?.results[0]?.id);

        setFormData((prevState) => ({
          ...prevState,
          shareHolders: response.attributes.results[0].shareHolders,
          isFormCompleted: response.attributes.results[0].isFormCompleted,
        }));
      }
    });
  }, []);

  const handleDateChange = (index, value) => {
    let isoString = "";
    let dateObj = null;

    if (typeof value === "string") {
      dateObj = parse(value, "dd/MM/yyyy", new Date());
    } else if (value instanceof Date && !isNaN(value)) {
      dateObj = value;
    }

    if (dateObj && isValid(dateObj)) {
      const utcDate = new Date(
        Date.UTC(dateObj.getFullYear(), dateObj.getMonth(), dateObj.getDate())
      );
      isoString = utcDate.toISOString();
    }

    setFormData((prevFormData) => {
      const updatedShareHolders = [...prevFormData.shareHolders];
      updatedShareHolders[index] = {
        ...updatedShareHolders[index],
        dateOfBirth: isoString,
      };

      return { ...prevFormData, shareHolders: updatedShareHolders };
    });
  };

  const handleDateInputChange = (index, val) => {
    setDateInputs((prev) => {
      const arr = [...prev];
      arr[index] = val;
      return arr;
    });
    if (!dateFormatRegex.test(val)) {
      setDateErrors((prev) => {
        const arr = [...prev];
        arr[index] = "Please enter date in dd/mm/yyyy format";
        return arr;
      });
      return;
    }
    const parsed = parse(val, "dd/MM/yyyy", new Date());
    if (isValid(parsed)) {
      // Age validation
      if (getAge(parsed) < 16) {
        setDateErrors((prev) => {
          const arr = [...prev];
          arr[index] = "Director must be at least 16 years old";
          return arr;
        });
        return;
      }
      handleDateChange(index, parsed);
    } else {
      setDateErrors((prev) => {
        const arr = [...prev];
        arr[index] = "Invalid date";
        return arr;
      });
    }
  };

  const getFilteredOptions = (index, type) => {
    const selectedTypes = formData.shareHolders.reduce((acc, shareholder) => {
      acc.push(
        shareholder.firstIdentificationType,
        shareholder.secondIdentificationType,
        shareholder.thirdIdentificationType
      );
      return acc;
    }, []);

    return consentType.filter(
      (option) =>
        !selectedTypes.includes(option) ||
        formData.shareHolders[index][type] === option
    );
  };

  const getFilteredOptionsForOtherIndex = (index, type) => {
    const selectedTypes = [];
    selectedTypes.push(formData.shareHolders[index].firstIdentificationType);
    selectedTypes.push(formData.shareHolders[index].secondIdentificationType);
    selectedTypes.push(formData.shareHolders[index].secondIdentificationType);

    return consentType.filter(
      (option) =>
        !selectedTypes.includes(option) ||
        formData.shareHolders[index][type] === option
    );
  };

  const handleSubmit = () => {
    // Prevent submission if any director is under 16
    if (
      dateErrors.some((err) => err === "Director must be at least 16 years old")
    ) {
      alert(
        "One or more directors are under 16 years old. Please correct the date(s) of birth."
      );
      return;
    }

    const newErrors = {};
    let isValid = true;

    // Check for duplicate email addresses
    const emailMap = new Map();
    const duplicateEmails = [];

    formData.shareHolders.forEach((shareholder, index) => {
      const email = shareholder.email.toLowerCase().trim();
      if (email) {
        if (emailMap.has(email)) {
          duplicateEmails.push(email);
          // Mark both occurrences of the email as errors
          newErrors[emailMap.get(email)] = {
            ...(newErrors[emailMap.get(email)] || {}),
            email: true,
            emailMessage: "Duplicate email address found",
          };
          newErrors[index] = {
            ...(newErrors[index] || {}),
            email: true,
            emailMessage: "Duplicate email address found",
          };
          isValid = false;
        } else {
          emailMap.set(email, index);
        }
      }
    });

    if (duplicateEmails.length > 0) {
      // Display alert for duplicate emails
      alert(
        "Duplicate email addresses found. Each director and share holder must have a unique email address."
      );
    }

    // Check for other required fields
    formData.shareHolders.forEach((shareholder, index) => {
      const {
        position,
        firstName,
        surName,
        nationality,
        countryOfResidence,
        email,
        mobileNumber,
        addressLine1,
        addressLine2,
        town,
        postCode,
        country,
        dateOfBirth,
        firstIdentificationAnswer,
        firstIdentificationType,
        secondIdentificationAnswer,
        secondIdentificationType,
        thirdIdentificationAnswer,
        thirdIdentificationType,
      } = shareholder;

      if (
        !position ||
        !firstName ||
        !surName ||
        !nationality ||
        !countryOfResidence ||
        !email ||
        !mobileNumber ||
        !addressLine1 ||
        !addressLine2 ||
        !town ||
        !postCode ||
        !country ||
        !dateOfBirth ||
        !firstIdentificationAnswer ||
        !firstIdentificationType ||
        !secondIdentificationAnswer ||
        !secondIdentificationType ||
        !thirdIdentificationAnswer ||
        !thirdIdentificationType
      ) {
        newErrors[index] = {
          ...(newErrors[index] || {}),
          position: !position,
          firstName: !firstName,
          surName: !surName,
          nationality: !nationality,
          countryOfResidence: !countryOfResidence,
          email: !email || newErrors[index]?.email || false,
          emailMessage:
            newErrors[index]?.emailMessage || "This field is required",
          mobileNumber: !mobileNumber,
          addressLine1: !addressLine1,
          addressLine2: !addressLine2,
          town: !town,
          postCode: !postCode,
          country: !country,
          dateOfBirth: !dateOfBirth,
          firstIdentificationAnswer: !firstIdentificationAnswer,
          firstIdentificationType: !firstIdentificationType,
          secondIdentificationAnswer: !secondIdentificationAnswer,
          secondIdentificationType: !secondIdentificationType,
          thirdIdentificationAnswer: !thirdIdentificationAnswer,
          thirdIdentificationType: !thirdIdentificationType,
        };
        isValid = false;
      }
    });

    if (!isValid) {
      setErrors(newErrors);
      if (duplicateEmails.length === 0) {
        alert("Please fill in all the required fields.");
      }
      return;
    }

    const hasAtLeastOneShareholder = formData.shareHolders.some((sh) =>
      sh.position.toLowerCase().includes("shareholder")
    );

    if (!hasAtLeastOneShareholder) {
      alert("At least one shareholder is required before submitting the form.");
      return;
    }

    setErrors({});
    if (!formData.isFormCompleted) {
      DirectorsApi({ ...formData, isFormCompleted: true }).then((res) => {
        if (res.status === 200) {
          updateFormStatus(formData.userId, {
            isDirectorDetailsCompleted: true,
          }).then((res) => {
            if (res.status === 200) {
              onContinue();
              localStorage.setItem("activeForm", "isDirectorDetailsCompleted");
            } else {
            }
          });
        }
      });
    } else {
      editDirectorsApi(userId, { ...formData }).then((res) => {
        if (res.status === 200) {
          onContinue();
          localStorage.setItem("activeForm", "isDirectorDetailsCompleted");
        }
      });
    }
  };

  const handleAddDirector = () => {
    const newDirector = {
      position: "",
      firstName: "",
      middleName: "",
      surName: "",
      nationality: "",
      countryOfResidence: "",
      email: "",
      mobileNumber: "",
      addressLine1: "",
      addressLine2: "",
      town: "",
      region: "",
      postCode: "",
      country: "",
      dateOfBirth: "",
      firstIdentificationType: "",
      firstIdentificationAnswer: "",
      secondIdentificationType: "",
      secondIdentificationAnswer: "",
      thirdIdentificationType: "",
      thirdIdentificationAnswer: "",
      noOfShare: "",
      shareCurrency: "GBP",
      shareClass: "Ordinary",
      amountPaidDuePerShare: "0",
      amountUnpaidPerShare: "1",
      isPsc: false,
      natureOfControl: "",
    };
    setFormData((prevFormData) => {
      const updatedShareHolders = [...prevFormData.shareHolders, newDirector];
      return { ...prevFormData, shareHolders: updatedShareHolders };
    });
    setDateInputs((prev) => [...prev, ""]);
    setTimeout(() => {
      if (shareHoldingRef.current) {
        shareHoldingRef.current.scrollIntoView({ behavior: "smooth" });
      }
    }, 0);
  };

  const handleRemoveDirector = (index) => {
    setFormData((prev) => ({
      ...prev,
      shareHolders: prev.shareHolders.slice(0, -1),
    }));
    setDateInputs((prev) => prev.slice(0, -1));
  };

  const parsedDate0 =
    dateInputs[0] && dateFormatRegex.test(dateInputs[0])
      ? parse(dateInputs[0], "dd/MM/yyyy", new Date())
      : formData.shareHolders[0]["dateOfBirth"]
      ? new Date(formData.shareHolders[0]["dateOfBirth"])
      : null;
  const isParsedValid0 = parsedDate0 && isValid(parsedDate0);

  return (
    <Box
      sx={{
        width: "100%",
        p: 3,
        borderRadius: 2,
        bgcolor: "background.paper",
        margin: "0 auto",
      }}
    >
      <Box>
        <Typography
          variant="h6"
          sx={{
            color: "1C2434",
            fontWeight: "bold",
            fontSize: "1.25rem",
          }}
        >
          Director 1
        </Typography>
        <Typography
          variant="body1"
          sx={{
            fontSize: "1rem",
            fontWeight: "medium",
            color: "#000000",
            mb: 1,
            mt: 2,
          }}
        >
          What will be your position in the company?
        </Typography>
        <Typography
          variant="body1"
          sx={{
            fontSize: "1rem",
            color: "gray",
            mb: 2,
          }}
        >
          You must have at least 1 Director & shareholder to operate.​
        </Typography>{" "}
        <FormControl
          variant="outlined"
          fullWidth
          required
          sx={{ maxWidth: "550px", width: "100%" }}
        >
          <Select
            name="position"
            value={formData.shareHolders[0]["position"] || ""}
            onChange={(e) => handleInputChange(0, e)}
            displayEmpty
            renderValue={(selected) => {
              return (
                selected || (
                  <span style={{ color: "gray" }}>Select position</span>
                )
              );
            }}
            sx={{
              maxHeight: "400px",
              borderRadius: "0.5rem",
            }}
            error={!!errors[0]?.position}
            helperText={errors[0]?.position ? "This field is required" : ""}
          >
            <MenuItem
              key={"Director + Shareholder"}
              value={"Director + Shareholder"}
              sx={{
                fontSize: "1rem",
                fontWeight: "medium",
                color: "64748B",
              }}
            >
              Director + Shareholder
            </MenuItem>
            <MenuItem
              key={"Director Only"}
              value={"Director Only"}
              sx={{
                fontSize: "1rem",
                fontWeight: "medium",
                color: "64748B",
              }}
            >
              Director Only
            </MenuItem>
            <MenuItem
              key={"Shareholder Only"}
              value={"Shareholder Only"}
              sx={{
                fontSize: "1rem",
                fontWeight: "medium",
                color: "64748B",
              }}
            >
              Shareholder Only
            </MenuItem>
          </Select>
          {errors[0]?.position && (
            <FormHelperText sx={{ color: "red" }}>
              {errors[0].position ? "This field is required" : ""}
            </FormHelperText>
          )}
        </FormControl>
        <Typography
          variant="body1"
          sx={{
            fontSize: "1rem",
            fontWeight: "medium",
            color: "#2F2D3B",
            marginTop: "1.5rem",
            marginBottom: "0.5rem",
            maxWidth: "745px",
            width: "100%",
          }}
        >
          Please provide your details below. These will not be made publicly
          available. ​
        </Typography>
        <Typography
          variant="body1"
          sx={{
            fontSize: "1rem",
            fontWeight: "medium",
            color: "2F2D3B",
            marginTop: "1.2rem",
            mb: 1,
          }}
        >
          First Name
        </Typography>
        <TextField
          name="firstName"
          fullWidth
          key="firstName"
          variant="outlined"
          value={formData.shareHolders[0]["firstName"] || ""}
          onChange={(e) => handleInputChange(0, e)}
          sx={{
            maxWidth: "555px",
            width: "100%",
          }}
          error={!!errors[0]?.firstName}
          helperText={errors[0]?.firstName ? "This field is required" : ""}
        />
        <Typography
          variant="body1"
          sx={{
            fontSize: "1rem",
            fontWeight: "medium",
            color: "2F2D3B",
            marginTop: "1.2rem",
            mb: 1,
          }}
        >
          Middle name
        </Typography>
        <TextField
          name="middleName"
          fullWidth
          key="middleName"
          variant="outlined"
          value={formData.shareHolders[0]["middleName"] || ""}
          onChange={(e) => handleInputChange(0, e)}
          sx={{ maxWidth: "555px", width: "100%" }}
          error={!!errors[0]?.middleName}
          helperText={errors[0]?.middleName ? "This field is required" : ""}
        />
        <Typography
          variant="body1"
          sx={{
            fontSize: "1rem",
            fontWeight: "medium",
            color: "2F2D3B",
            marginTop: "1.2rem",
            mb: 1,
          }}
        >
          Last Name
        </Typography>
        <TextField
          name="surName"
          fullWidth
          key="surName"
          variant="outlined"
          value={formData.shareHolders[0]["surName"] || ""}
          onChange={(e) => handleInputChange(0, e)}
          sx={{ maxWidth: "555px", width: "100%" }}
          error={!!errors[0]?.surName}
          helperText={errors[0]?.surName ? "This field is required" : ""}
        />
        <Typography
          variant="body1"
          sx={{
            fontSize: "1rem",
            fontWeight: "medium",
            color: "2F2D3B",
            marginTop: "1.2rem",
            mb: 1,
          }}
        >
          Nationality
        </Typography>
        <FormControl
          variant="outlined"
          fullWidth
          required
          sx={{ maxWidth: "550px", width: "100%", mb: 1 }}
        >
          <Select
            name="nationality"
            value={formData.shareHolders[0].nationality || ""}
            onChange={(e) => handleInputChange(0, e)}
            variant="outlined"
            sx={{ maxWidth: "555px", width: "100%" }}
            error={!!errors[0]?.nationality}
          >
            {nationalityList.map((nationality) => (
              <MenuItem
                key={nationality}
                value={nationality}
                sx={{
                  fontSize: "1rem",
                  fontWeight: "medium",
                  color: "64748B",
                }}
              >
                {nationality}
              </MenuItem>
            ))}
          </Select>
          {errors[0]?.nationality && (
            <FormHelperText sx={{ color: "red" }}>
              This field is required
            </FormHelperText>
          )}
        </FormControl>
        <Typography
          variant="body1"
          sx={{
            fontSize: "1rem",
            fontWeight: "medium",
            color: "2F2D3B",
            marginTop: "1.2rem",
            mb: 1,
          }}
        >
          Country Of Residence
        </Typography>
        <FormControl
          variant="outlined"
          fullWidth
          required
          sx={{ maxWidth: "550px", width: "100%", mb: 1 }}
        >
          <Select
            name="countryOfResidence"
            value={formData.shareHolders[0].countryOfResidence || ""}
            onChange={(e) => handleInputChange(0, e)}
            variant="outlined"
            sx={{ maxWidth: "555px", width: "100%" }}
            error={!!errors[0]?.countryOfResidence}
          >
            {countryResidenceList.map((residence) => (
              <MenuItem
                key={residence}
                value={residence}
                sx={{
                  fontSize: "1rem",
                  fontWeight: "medium",
                  color: "64748B",
                }}
              >
                {residence}
              </MenuItem>
            ))}
          </Select>
          {errors[0]?.countryOfResidence && (
            <FormHelperText sx={{ color: "red" }}>
              This field is required
            </FormHelperText>
          )}
        </FormControl>
        <Typography
          variant="body1"
          sx={{
            fontSize: "1rem",
            fontWeight: "medium",
            color: "2F2D3B",
            marginTop: "1.2rem",
            mb: 1,
          }}
        >
          Email address
        </Typography>
        <TextField
          name="email"
          fullWidth
          key="email"
          variant="outlined"
          value={formData.shareHolders[0]["email"] || ""}
          onChange={(e) => handleInputChange(0, e)}
          sx={{ maxWidth: "555px", width: "100%" }}
          error={!!errors[0]?.email}
          helperText={errors[0]?.email ? "This field is required" : ""}
        />
        <Typography
          variant="body1"
          sx={{
            fontSize: "1rem",
            fontWeight: "medium",
            color: "2F2D3B",
            marginTop: "1.2rem",
            mb: 1,
          }}
        >
          Mobile number
        </Typography>
        <TextField
          name="mobileNumber"
          type="number"
          fullWidth
          key="mobileNumber"
          variant="outlined"
          value={formData.shareHolders[0]["mobileNumber"] || ""}
          onChange={(e) => handleInputChange(0, e)}
          sx={{ maxWidth: "555px", width: "100%" }}
          error={!!errors[0]?.mobileNumber}
          helperText={errors[0]?.mobileNumber ? "This field is required" : ""}
        />
        <Typography
          variant="body1"
          sx={{
            fontSize: "1rem",
            fontWeight: "bold",
            color: "#2F2D3B",
            marginTop: "2rem",
            marginBottom: "0.5rem",
            maxWidth: "745px",
            width: "100%",
          }}
        >
          Home Address
        </Typography>
        <Typography
          variant="body1"
          sx={{
            fontSize: "1rem",
            fontWeight: "medium",
            color: "2F2D3B",
            marginTop: "1.2rem",
            mb: 1,
          }}
        >
          First line of address
        </Typography>
        <TextField
          name="addressLine1"
          fullWidth
          key="addressLine1"
          variant="outlined"
          value={formData.shareHolders[0]["addressLine1"] || ""}
          onChange={(e) => handleInputChange(0, e)}
          sx={{ maxWidth: "555px", width: "100%" }}
          error={!!errors[0]?.addressLine1}
          helperText={errors[0]?.addressLine1 ? "This field is required" : ""}
        />
        <Typography
          variant="body1"
          sx={{
            fontSize: "1rem",
            fontWeight: "medium",
            color: "2F2D3B",
            marginTop: "1.2rem",
            mb: 1,
          }}
        >
          Second line of address
        </Typography>
        <TextField
          name="addressLine2"
          fullWidth
          key="addressLine2"
          variant="outlined"
          value={formData.shareHolders[0]["addressLine2"] || ""}
          onChange={(e) => handleInputChange(0, e)}
          sx={{ maxWidth: "555px", width: "100%" }}
          error={!!errors[0]?.addressLine2}
          helperText={errors[0]?.addressLine2 ? "This field is required" : ""}
        />
        <Typography
          variant="body1"
          sx={{
            fontSize: "1rem",
            fontWeight: "medium",
            color: "2F2D3B",
            marginTop: "1.2rem",
            mb: 1,
          }}
        >
          Town
        </Typography>
        <TextField
          name="town"
          fullWidth
          key="town"
          variant="outlined"
          value={formData.shareHolders[0]["town"] || ""}
          onChange={(e) => handleInputChange(0, e)}
          sx={{ maxWidth: "555px", width: "100%" }}
          error={!!errors[0]?.town}
          helperText={errors[0]?.town ? "This field is required" : ""}
        />
        <Typography
          variant="body1"
          sx={{
            fontSize: "1rem",
            fontWeight: "medium",
            color: "2F2D3B",
            marginTop: "1.2rem",
            mb: 1,
          }}
        >
          Postcode
        </Typography>
        <TextField
          name="postCode"
          fullWidth
          key="postCode"
          variant="outlined"
          value={formData.shareHolders[0]["postCode"] || ""}
          onChange={(e) => handleInputChange(0, e)}
          sx={{ maxWidth: "555px", width: "100%" }}
          error={!!errors[0]?.postCode}
          helperText={errors[0]?.postCode ? "This field is required" : ""}
        />
        <Typography
          sx={{
            fontSize: "1rem",
            fontWeight: "medium",
            mb: 1,
            marginTop: "1.2rem",
          }}
        >
          Country
        </Typography>
        <FormControl
          variant="outlined"
          fullWidth
          required
          sx={{ maxWidth: "550px", width: "100%", mb: 1 }}
        >
          <Select
            name="country"
            value={formData.shareHolders[0]?.country || ""}
            onChange={(e) => {
              const selectedCountryName = e.target.value;
              const selectedCountry = counntries.find(
                (c) => c.name === selectedCountryName
              );

              setFormData((prevFormData) => {
                const updatedShareHolders = [...prevFormData.shareHolders];
                updatedShareHolders[0] = {
                  ...updatedShareHolders[0],
                  country: selectedCountry?.name || "",
                  region: selectedCountry?.code,
                };

                return {
                  ...prevFormData,
                  shareHolders: updatedShareHolders,
                };
              });
            }}
            variant="outlined"
            sx={{ maxWidth: "555px", width: "100%" }}
            error={!!errors[0]?.country}
          >
            {counntries?.map((country, index) => (
              <MenuItem
                key={index}
                value={country.name}
                sx={{
                  fontSize: "1rem",
                  fontWeight: "medium",
                  color: "64748B",
                }}
              >
                {country.name}
              </MenuItem>
            ))}
          </Select>
          {errors[0]?.country && (
            <FormHelperText sx={{ color: "red" }}>
              This field is required
            </FormHelperText>
          )}
        </FormControl>
        {formData.shareHolders[0]["country"] === "United Kingdom" && (
          <>
            <Typography
              sx={{
                fontSize: "1rem",
                fontWeight: "medium",
                mb: 1,
                marginTop: "1.2rem",
              }}
            >
              Region
            </Typography>
            <FormControl
              variant="outlined"
              fullWidth
              required
              sx={{ maxWidth: "550px", width: "100%", mb: 1 }}
            >
              <Select
                name="region"
                value={formData.shareHolders[0]["region"] || ""}
                onChange={(e) => handleInputChange(0, e)}
                key="region"
                variant="outlined"
                sx={{ maxWidth: "555px", width: "100%" }}
                error={!!errors[0]?.region}
                helperText={errors[0]?.region ? "This field is required" : ""}
              >
                <MenuItem
                  value="GB-ENG"
                  sx={{
                    fontSize: "1rem",
                    fontWeight: "medium",
                    color: "64748B",
                  }}
                >
                  England
                </MenuItem>
                <MenuItem
                  value="GB-WLS"
                  sx={{
                    fontSize: "1rem",
                    fontWeight: "medium",
                    color: "64748B",
                  }}
                >
                  Wales
                </MenuItem>
                <MenuItem
                  value="GB-SCT"
                  sx={{
                    fontSize: "1rem",
                    fontWeight: "medium",
                    color: "64748B",
                  }}
                >
                  Scotland
                </MenuItem>
                <MenuItem
                  value="GB-NIR"
                  sx={{
                    fontSize: "1rem",
                    fontWeight: "medium",
                    color: "64748B",
                  }}
                >
                  Northern Ireland
                </MenuItem>
              </Select>
              {errors[0]?.region && (
                <FormHelperText sx={{ color: "red" }}>
                  {errors[0].region ? "This field is required" : ""}
                </FormHelperText>
              )}
            </FormControl>
          </>
        )}
        <Typography
          variant="body1"
          sx={{
            fontSize: "1rem",
            fontWeight: "medium",
            color: "#000000",
            marginBottom: "1rem",
            marginTop: "1.2rem",
          }}
        >
          Date of Birth
        </Typography>
        <ReactDatePicker
          selected={isParsedValid0 ? parsedDate0 : null}
          onChange={(date) => {
            if (isValid(date)) {
              if (getAge(date) < 16) {
                setDateErrors((prev) => {
                  const arr = [...prev];
                  arr[0] = "Director must be at least 16 years old";
                  return arr;
                });
                return;
              }
              const formatted = format(date, "dd/MM/yyyy");
              handleDateChange(0, date);
              setDateInputs((prev) => {
                const arr = [...prev];
                arr[0] = formatted;
                return arr;
              });
              setDateErrors((prev) => {
                const arr = [...prev];
                arr[0] = "";
                return arr;
              });
            }
          }}
          dateFormat="dd/MM/yyyy"
          customInput={
            <DateMaskedInput
              error={!!errors[0]?.dateOfBirth || !!dateErrors[0]}
              helperText={
                errors[0]?.dateOfBirth
                  ? "This field is required"
                  : dateErrors[0]
                  ? dateErrors[0]
                  : ""
              }
            />
          }
          onChangeRaw={(e) => handleDateInputChange(0, e.target.value)}
          placeholderText="dd/mm/yyyy"
        />
        <Typography
          variant="body1"
          sx={{
            fontSize: "1rem",
            fontWeight: "bold",
            color: "#2F2D3B",
            marginTop: "2rem",
            marginBottom: "0.5rem",
            maxWidth: "745px",
            width: "100%",
          }}
        >
          Companies House Identification items​
        </Typography>
        <Typography
          variant="body1"
          sx={{
            fontSize: "1rem",
            color: "#787878",
            mb: 2,
            maxWidth: "555px",
            width: "100%",
          }}
        >
          For security purposes, Companies House require you provide at least 3
          of the following identification responses: ​
        </Typography>
        <Typography
          variant="body1"
          sx={{
            fontSize: "1rem",
            fontWeight: "medium",
            color: "#000000",
            mb: 1,
          }}
        >
          First identification item
        </Typography>
        <FormControl sx={{ maxWidth: "555px", width: "100%", mb: 1.5, mr: 10 }}>
          <InputLabel>Type</InputLabel>
          <Select
            value={formData.shareHolders[0].firstIdentificationType}
            onChange={(e) => handleInputChange(0, e)}
            label="Type"
            name="firstIdentificationType"
            error={!!errors[0]?.firstIdentificationType}
            helperText={
              errors[0]?.firstIdentificationType ? "This field is required" : ""
            }
          >
            {getFilteredOptions(0, "firstIdentificationType").map(
              (consentType) => (
                <MenuItem key={consentType} value={consentType}>
                  {consentType}
                </MenuItem>
              )
            )}
          </Select>
          {errors[0]?.firstIdentificationType && (
            <FormHelperText sx={{ color: "red" }}>
              {errors[0].firstIdentificationType
                ? "This field is required"
                : ""}
            </FormHelperText>
          )}
        </FormControl>
        <Typography
          variant="body1"
          sx={{
            fontSize: "1rem",
            fontWeight: "medium",
            color: "#2F2D3B",
            maxWidth: "745px",
            width: "100%",
          }}
        >
          Answer
        </Typography>
        <TextField
          label="Answer"
          name="firstIdentificationAnswer"
          value={formData.shareHolders[0].firstIdentificationAnswer}
          onChange={(e) => handleInputChange(0, e)}
          error={!!errors[0]?.firstIdentificationAnswer}
          helperText={
            errors[0]?.firstIdentificationAnswer ? "This field is required" : ""
          }
          sx={{
            maxWidth: "555px",
            width: "100%",
            mb: "1.2rem",
            mt: "0.8rem",
          }}
        />
        <Typography
          variant="body1"
          sx={{
            fontSize: "1rem",
            fontWeight: "medium",
            color: "#000000",
            mb: 1,
          }}
        >
          Second identification item
        </Typography>
        <FormControl sx={{ maxWidth: "555px", width: "100%", mb: 1.5, mr: 10 }}>
          <InputLabel>Type</InputLabel>
          <Select
            value={formData.shareHolders[0].secondIdentificationType}
            onChange={(e) => handleInputChange(0, e)}
            name="secondIdentificationType"
            label="Type"
            error={!!errors[0]?.secondIdentificationType}
            helperText={
              errors[0]?.secondIdentificationType
                ? "This field is required"
                : ""
            }
          >
            {getFilteredOptions(0, "secondIdentificationType").map(
              (consentType) => (
                <MenuItem key={consentType} value={consentType}>
                  {consentType}
                </MenuItem>
              )
            )}
          </Select>
          {errors[0]?.secondIdentificationType && (
            <FormHelperText sx={{ color: "red" }}>
              {errors[0].secondIdentificationType
                ? "This field is required"
                : ""}
            </FormHelperText>
          )}
        </FormControl>
        <Typography
          variant="body1"
          sx={{
            fontSize: "1rem",
            fontWeight: "medium",
            color: "#2F2D3B",
            maxWidth: "745px",
            width: "100%",
          }}
        >
          Answer
        </Typography>
        <TextField
          label="Answer"
          name="secondIdentificationAnswer"
          value={formData.shareHolders[0].secondIdentificationAnswer}
          onChange={(e) => handleInputChange(0, e)}
          error={!!errors[0]?.secondIdentificationAnswer}
          helperText={
            errors[0]?.secondIdentificationAnswer
              ? "This field is required"
              : ""
          }
          sx={{
            maxWidth: "555px",
            width: "100%",
            mb: "1.2rem",
            mt: "0.8rem",
          }}
        />
        <Typography
          variant="body1"
          sx={{
            fontSize: "1rem",
            fontWeight: "medium",
            color: "#000000",
            mb: 1,
          }}
        >
          Third identification item
        </Typography>
        <FormControl sx={{ maxWidth: "555px", width: "100%", mb: 1.5, mr: 10 }}>
          <InputLabel>Type</InputLabel>
          <Select
            value={formData.shareHolders[0].thirdIdentificationType}
            onChange={(e) => handleInputChange(0, e)}
            label="Type"
            name="thirdIdentificationType"
            error={!!errors[0]?.thirdIdentificationType}
            helperText={
              errors[0]?.thirdIdentificationAnswer
                ? "This field is required"
                : ""
            }
          >
            {getFilteredOptions(0, "thirdIdentificationType").map(
              (consentType) => (
                <MenuItem key={consentType} value={consentType}>
                  {consentType}
                </MenuItem>
              )
            )}
          </Select>
          {errors[0]?.thirdIdentificationType && (
            <FormHelperText sx={{ color: "red" }}>
              {errors[0].thirdIdentificationType
                ? "This field is required"
                : ""}
            </FormHelperText>
          )}
        </FormControl>
        <Typography
          variant="body1"
          sx={{
            fontSize: "1rem",
            fontWeight: "medium",
            color: "#2F2D3B",
            maxWidth: "745px",
            width: "100%",
          }}
        >
          Answer
        </Typography>
        <TextField
          label="Answer"
          name="thirdIdentificationAnswer"
          value={formData.shareHolders[0].thirdIdentificationAnswer}
          onChange={(e) => handleInputChange(0, e)}
          error={!!errors[0]?.thirdIdentificationAnswer}
          helperText={
            errors[0]?.thirdIdentificationAnswer ? "This field is required" : ""
          }
          sx={{
            maxWidth: "555px",
            width: "100%",
            mb: "1.2rem",
            mt: "0.8rem",
          }}
        />
      </Box>
      {formData.shareHolders.slice(1).map((shareholder, index) => {
        const parsedDate =
          dateInputs[index + 1] && dateFormatRegex.test(dateInputs[index + 1])
            ? parse(dateInputs[index + 1], "dd/MM/yyyy", new Date())
            : formData.shareHolders[index + 1]["dateOfBirth"]
            ? new Date(formData.shareHolders[index + 1]["dateOfBirth"])
            : null;
        const isParsedValid = parsedDate && isValid(parsedDate);
        return (
          <Box key={index} ref={shareHoldingRef}>
            <Typography
              variant="h6"
              sx={{
                color: "1C2434",
                fontWeight: "bold",
                fontSize: "1.25rem",
              }}
            >
              Director {index + 2}
            </Typography>
            <Typography
              variant="body1"
              sx={{
                fontSize: "1rem",
                fontWeight: "medium",
                color: "#000000",
                mb: 1,
                mt: 2,
              }}
            >
              What will be your position in the company?
            </Typography>
            <Typography
              variant="body1"
              sx={{
                fontSize: "1rem",
                color: "gray",
                mb: 2,
              }}
            >
              You must have at least 1 Director & shareholder to operate.​
            </Typography>{" "}
            <FormControl
              variant="outlined"
              fullWidth
              required
              sx={{ maxWidth: "550px", width: "100%" }}
            >
              <Select
                name="position"
                value={formData.shareHolders[index + 1]["position"] || ""}
                onChange={(e) => handleInputChange(index + 1, e)}
                displayEmpty
                renderValue={(selected) => {
                  return (
                    selected || (
                      <span style={{ color: "gray" }}>Select position</span>
                    )
                  );
                }}
                error={!!errors[index + 1]?.position}
                helperText={
                  errors[index + 1]?.position ? "This field is required" : ""
                }
                sx={{ maxHeight: "400px", borderRadius: "0.5rem" }}
              >
                <MenuItem
                  key={"Director + Shareholder"}
                  value={"Director + Shareholder"}
                  sx={{
                    fontSize: "1rem",
                    fontWeight: "medium",
                    color: "64748B",
                  }}
                >
                  Director + Shareholder
                </MenuItem>
                <MenuItem
                  key={"Director Only"}
                  value={"Director Only"}
                  sx={{
                    fontSize: "1rem",
                    fontWeight: "medium",
                    color: "64748B",
                  }}
                >
                  Director Only
                </MenuItem>
                <MenuItem
                  key={"Shareholder Only"}
                  value={"Shareholder Only"}
                  sx={{
                    fontSize: "1rem",
                    fontWeight: "medium",
                    color: "64748B",
                  }}
                >
                  Shareholder Only
                </MenuItem>
              </Select>
              {errors[index + 1]?.position && (
                <FormHelperText sx={{ color: "red" }}>
                  {errors[index + 1].position ? "This field is required" : ""}
                </FormHelperText>
              )}
            </FormControl>
            {personKnown === "yes" && (
              <>
                <Typography
                  variant="body1"
                  sx={{
                    fontSize: "1rem",
                    fontWeight: "medium",
                    color: "#000000",
                  }}
                >
                  Previous Forename(s)
                </Typography>
                <TextField
                  fullWidth
                  variant="outlined"
                  name="previousForename"
                  value={formData.previousForename || ""}
                  onChange={(e) => handleInputChange(index + 1, e)}
                  sx={{
                    maxWidth: "555px",
                    width: "100%",
                    mb: "1rem",
                    mt: "1rem",
                  }}
                />{" "}
                <Typography
                  variant="body1"
                  sx={{
                    fontSize: "1rem",
                    fontWeight: "medium",
                    color: "#000000",
                  }}
                >
                  Previous Surnames
                </Typography>
                <TextField
                  fullWidth
                  variant="outlined"
                  name="previousSurname"
                  value={
                    formData.shareHolders[index + 1]["previousSurname"] || ""
                  }
                  onChange={(e) => handleInputChange(index + 1, e)}
                  sx={{
                    maxWidth: "555px",
                    width: "100%",
                    mb: "2rem",
                    mt: "1rem",
                  }}
                />
              </>
            )}
            <Typography
              variant="body1"
              sx={{
                fontSize: "1rem",
                fontWeight: "medium",
                color: "#2F2D3B",
                marginTop: "1.5rem",
                marginBottom: "0.5rem",
                maxWidth: "745px",
                width: "100%",
              }}
            >
              Please provide your details below. These will not be made publicly
              available. ​
            </Typography>
            <Typography
              variant="body1"
              sx={{
                fontSize: "1rem",
                fontWeight: "medium",
                color: "2F2D3B",
                marginTop: "1.2rem",
                mb: 1,
              }}
            >
              First Name
            </Typography>
            <TextField
              name="firstName"
              fullWidth
              key="firstName"
              variant="outlined"
              value={formData.shareHolders[index + 1]["firstName"] || ""}
              onChange={(e) => handleInputChange(index + 1, e)}
              sx={{ maxWidth: "555px", width: "100%" }}
              error={!!errors[index + 1]?.firstName}
              helperText={
                errors[index + 1]?.firstName ? "This field is required" : ""
              }
            />
            <Typography
              variant="body1"
              sx={{
                fontSize: "1rem",
                fontWeight: "medium",
                color: "2F2D3B",
                marginTop: "1.2rem",
                mb: 1,
              }}
            >
              Middle name
            </Typography>
            <TextField
              name="middleName"
              fullWidth
              key="middleName"
              variant="outlined"
              value={formData.shareHolders[index + 1]["middleName"] || ""}
              onChange={(e) => handleInputChange(index + 1, e)}
              sx={{ maxWidth: "555px", width: "100%" }}
              error={!!errors[index + 1]?.middleName}
              helperText={
                errors[index + 1]?.middleName ? "This field is required" : ""
              }
            />
            <Typography
              variant="body1"
              sx={{
                fontSize: "1rem",
                fontWeight: "medium",
                color: "2F2D3B",
                marginTop: "1.2rem",
                mb: 1,
              }}
            >
              Last Name
            </Typography>
            <TextField
              name="surName"
              fullWidth
              key="surName"
              variant="outlined"
              value={formData.shareHolders[index + 1]["surName"] || ""}
              onChange={(e) => handleInputChange(index + 1, e)}
              sx={{ maxWidth: "555px", width: "100%" }}
              error={!!errors[index + 1]?.surName}
              helperText={
                errors[index + 1]?.surName ? "This field is required" : ""
              }
            />
            <Typography
              variant="body1"
              sx={{
                fontSize: "1rem",
                fontWeight: "medium",
                color: "2F2D3B",
                marginTop: "1.2rem",
                mb: 1,
              }}
            >
              Nationality
            </Typography>
            <FormControl
              variant="outlined"
              fullWidth
              required
              sx={{ maxWidth: "550px", width: "100%", mb: 1 }}
            >
              <Select
                name="nationality"
                value={formData.shareHolders[index + 1].nationality || ""}
                onChange={(e) => handleInputChange(index + 1, e)}
                variant="outlined"
                sx={{ maxWidth: "555px", width: "100%" }}
                error={!!errors[index + 1]?.nationality}
              >
                {nationalityList.map((nationality) => (
                  <MenuItem
                    key={nationality}
                    value={nationality}
                    sx={{
                      fontSize: "1rem",
                      fontWeight: "medium",
                      color: "64748B",
                    }}
                  >
                    {nationality}
                  </MenuItem>
                ))}
              </Select>
              {errors[index + 1]?.nationality && (
                <FormHelperText sx={{ color: "red" }}>
                  This field is required
                </FormHelperText>
              )}
            </FormControl>
            <Typography
              variant="body1"
              sx={{
                fontSize: "1rem",
                fontWeight: "medium",
                color: "2F2D3B",
                marginTop: "1.2rem",
                mb: 1,
              }}
            >
              Country Of Residence
            </Typography>
            <FormControl
              variant="outlined"
              fullWidth
              required
              sx={{ maxWidth: "550px", width: "100%", mb: 1 }}
            >
              <Select
                name="countryOfResidence"
                value={
                  formData.shareHolders[index + 1].countryOfResidence || ""
                }
                onChange={(e) => handleInputChange(index + 1, e)}
                variant="outlined"
                sx={{ maxWidth: "555px", width: "100%" }}
                error={!!errors[index + 1]?.countryOfResidence}
              >
                {countryResidenceList.map((residence) => (
                  <MenuItem
                    key={residence}
                    value={residence}
                    sx={{
                      fontSize: "1rem",
                      fontWeight: "medium",
                      color: "64748B",
                    }}
                  >
                    {residence}
                  </MenuItem>
                ))}
              </Select>
              {errors[index + 1]?.countryOfResidence && (
                <FormHelperText sx={{ color: "red" }}>
                  This field is required
                </FormHelperText>
              )}
            </FormControl>
            <Typography
              variant="body1"
              sx={{
                fontSize: "1rem",
                fontWeight: "medium",
                color: "2F2D3B",
                marginTop: "1.2rem",
                mb: 1,
              }}
            >
              Email address
            </Typography>
            <TextField
              name="email"
              fullWidth
              key="email"
              variant="outlined"
              value={formData.shareHolders[index + 1]["email"] || ""}
              onChange={(e) => handleInputChange(index + 1, e)}
              sx={{ maxWidth: "555px", width: "100%" }}
              error={!!errors[index + 1]?.email}
              helperText={
                errors[index + 1]?.email ? "This field is required" : ""
              }
            />
            <Typography
              variant="body1"
              sx={{
                fontSize: "1rem",
                fontWeight: "medium",
                color: "2F2D3B",
                marginTop: "1.2rem",
                mb: 1,
              }}
            >
              Mobile number
            </Typography>
            <TextField
              name="mobileNumber"
              fullWidth
              key="mobileNumber"
              variant="outlined"
              value={formData.shareHolders[index + 1]["mobileNumber"] || ""}
              onChange={(e) => handleInputChange(index + 1, e)}
              sx={{ maxWidth: "555px", width: "100%" }}
              error={!!errors[index + 1]?.mobileNumber}
              helperText={
                errors[index + 1]?.mobileNumber ? "This field is required" : ""
              }
            />
            <Typography
              variant="body1"
              sx={{
                fontSize: "1rem",
                fontWeight: "bold",
                color: "#2F2D3B",
                marginTop: "2rem",
                marginBottom: "0.5rem",
                maxWidth: "745px",
                width: "100%",
              }}
            >
              Home Address
            </Typography>
            <Typography
              variant="body1"
              sx={{
                fontSize: "1rem",
                fontWeight: "medium",
                color: "2F2D3B",
                marginTop: "1.2rem",
                mb: 1,
              }}
            >
              First line of address
            </Typography>
            <TextField
              name="addressLine1"
              fullWidth
              key="addressLine1"
              variant="outlined"
              value={formData.shareHolders[index + 1]["addressLine1"] || ""}
              onChange={(e) => handleInputChange(index + 1, e)}
              sx={{ maxWidth: "555px", width: "100%" }}
              error={!!errors[index + 1]?.addressLine1}
              helperText={
                errors[index + 1]?.addressLine1 ? "This field is required" : ""
              }
            />
            <Typography
              variant="body1"
              sx={{
                fontSize: "1rem",
                fontWeight: "medium",
                color: "2F2D3B",
                marginTop: "1.2rem",
                mb: 1,
              }}
            >
              Second line of address
            </Typography>
            <TextField
              name="addressLine2"
              fullWidth
              key="addressLine2"
              variant="outlined"
              value={formData.shareHolders[index + 1]["addressLine2"] || ""}
              onChange={(e) => handleInputChange(index + 1, e)}
              sx={{ maxWidth: "555px", width: "100%" }}
              error={!!errors[index + 1]?.addressLine2}
              helperText={
                errors[index + 1]?.addressLine2 ? "This field is required" : ""
              }
            />
            <Typography
              variant="body1"
              sx={{
                fontSize: "1rem",
                fontWeight: "medium",
                color: "2F2D3B",
                marginTop: "1.2rem",
                mb: 1,
              }}
            >
              Town
            </Typography>
            <TextField
              name="town"
              fullWidth
              key="town"
              variant="outlined"
              value={formData.shareHolders[index + 1]["town"] || ""}
              onChange={(e) => handleInputChange(index + 1, e)}
              sx={{ maxWidth: "555px", width: "100%" }}
              error={!!errors[index + 1]?.town}
              helperText={
                errors[index + 1]?.town ? "This field is required" : ""
              }
            />
            <Typography
              variant="body1"
              sx={{
                fontSize: "1rem",
                fontWeight: "medium",
                color: "2F2D3B",
                marginTop: "1.2rem",
                mb: 1,
              }}
            >
              Postcode
            </Typography>
            <TextField
              name="postCode"
              fullWidth
              key="postCode"
              variant="outlined"
              value={formData.shareHolders[index + 1]["postCode"] || ""}
              onChange={(e) => handleInputChange(index + 1, e)}
              sx={{ maxWidth: "555px", width: "100%" }}
              error={!!errors[index + 1]?.postCode}
              helperText={
                errors[index + 1]?.postCode ? "This field is required" : ""
              }
            />
            <Typography
              sx={{
                fontSize: "1rem",
                fontWeight: "medium",
                mb: 1,
              }}
              gutterBottom
            >
              Country
            </Typography>
            <FormControl
              variant="outlined"
              fullWidth
              required
              sx={{ maxWidth: "550px", width: "100%", mb: 1 }}
            >
              <Select
                name="country"
                value={formData.shareHolders[index + 1]["country"] || ""}
                onChange={(e) => {
                  const selectedCountryName = e.target.value;
                  const selectedCountry = counntries.find(
                    (c) => c.name === selectedCountryName
                  );

                  setFormData((prevFormData) => {
                    const updatedShareHolders = [...prevFormData.shareHolders];
                    updatedShareHolders[index + 1] = {
                      ...updatedShareHolders[index + 1],
                      country: selectedCountry?.name || "",
                      region: selectedCountry?.code,
                    };

                    return {
                      ...prevFormData,
                      shareHolders: updatedShareHolders,
                    };
                  });
                }}
                key="country"
                variant="outlined"
                sx={{ maxWidth: "555px", width: "100%" }}
                error={!!errors[index + 1]?.country}
                helperText={
                  errors[index + 1]?.country ? "This field is required" : ""
                }
              >
                {counntries?.length > 0 &&
                  counntries.map((country, index) => (
                    <MenuItem
                      key={index}
                      value={country.name}
                      sx={{
                        fontSize: "1rem",
                        fontWeight: "medium",
                        color: "64748B",
                      }}
                    >
                      {country.name}
                    </MenuItem>
                  ))}
              </Select>
              {errors[index + 1]?.country && (
                <FormHelperText sx={{ color: "red" }}>
                  {errors[index + 1].country ? "This field is required" : ""}
                </FormHelperText>
              )}
            </FormControl>
            {formData.shareHolders[index + 1].country === "United Kingdom" && (
              <>
                <Typography
                  sx={{
                    fontSize: "1rem",
                    fontWeight: "medium",
                    mb: 1,
                  }}
                  gutterBottom
                >
                  Region
                </Typography>
                <FormControl
                  variant="outlined"
                  fullWidth
                  required
                  sx={{
                    maxWidth: "550px",
                    width: "100%",
                    mb: 1,
                  }}
                >
                  <Select
                    name="region"
                    value={formData.shareHolders[index + 1]["region"] || ""}
                    onChange={(e) => handleInputChange(index + 1, e)}
                    key="region"
                    variant="outlined"
                    sx={{ maxWidth: "555px", width: "100%" }}
                    error={!!errors[index + 1]?.region}
                    helperText={
                      errors[index + 1]?.region ? "This field is required" : ""
                    }
                  >
                    <MenuItem
                      value="GB-ENG"
                      sx={{
                        fontSize: "1rem",
                        fontWeight: "medium",
                        color: "64748B",
                      }}
                    >
                      England
                    </MenuItem>
                    <MenuItem
                      value="GB-WLS"
                      sx={{
                        fontSize: "1rem",
                        fontWeight: "medium",
                        color: "64748B",
                      }}
                    >
                      Wales
                    </MenuItem>
                    <MenuItem
                      value="GB-SCT"
                      sx={{
                        fontSize: "1rem",
                        fontWeight: "medium",
                        color: "64748B",
                      }}
                    >
                      Scotland
                    </MenuItem>
                    <MenuItem
                      value="GB-NIR"
                      sx={{
                        fontSize: "1rem",
                        fontWeight: "medium",
                        color: "64748B",
                      }}
                    >
                      Northern Ireland
                    </MenuItem>
                  </Select>
                  {errors[index + 1]?.region && (
                    <FormHelperText sx={{ color: "red" }}>
                      {errors[index + 1].region ? "This field is required" : ""}
                    </FormHelperText>
                  )}
                </FormControl>
              </>
            )}
            <Typography
              variant="body1"
              sx={{
                fontSize: "1rem",
                fontWeight: "medium",
                color: "#000000",
                marginBottom: "1rem",
                marginTop: "1.2rem",
              }}
            >
              Date of Birth
            </Typography>
            <ReactDatePicker
              selected={isParsedValid ? parsedDate : null}
              onChange={(date) => {
                if (isValid(date)) {
                  if (getAge(date) < 16) {
                    setDateErrors((prev) => {
                      const arr = [...prev];
                      arr[index + 1] = "Director must be at least 16 years old";
                      return arr;
                    });
                    return;
                  }
                  const formatted = format(date, "dd/MM/yyyy");
                  handleDateChange(index + 1, date);
                  setDateInputs((prev) => {
                    const arr = [...prev];
                    arr[index + 1] = formatted;
                    return arr;
                  });
                  setDateErrors((prev) => {
                    const arr = [...prev];
                    arr[index + 1] = "";
                    return arr;
                  });
                }
              }}
              dateFormat="dd/MM/yyyy"
              customInput={
                <DateMaskedInput
                  error={
                    !!errors[index + 1]?.dateOfBirth || !!dateErrors[index + 1]
                  }
                  helperText={
                    errors[index + 1]?.dateOfBirth
                      ? "This field is required"
                      : dateErrors[index + 1]
                      ? dateErrors[index + 1]
                      : ""
                  }
                />
              }
              onChangeRaw={(e) =>
                handleDateInputChange(index + 1, e.target.value)
              }
              placeholderText="dd/mm/yyyy"
            />
            <Typography
              variant="body1"
              sx={{
                fontSize: "1rem",
                fontWeight: "bold",
                color: "#2F2D3B",
                marginTop: "2rem",
                marginBottom: "0.5rem",
                maxWidth: "745px",
                width: "100%",
              }}
            >
              Companies House Identification items​
            </Typography>
            <Typography
              variant="body1"
              sx={{
                fontSize: "1rem",
                color: "#787878",
                mb: 2,
                maxWidth: "555px",
                width: "100%",
              }}
            >
              For security purposes, Companies House require you provide at
              least 3 of the following identification responses: ​
            </Typography>
            <Typography
              variant="body1"
              sx={{
                fontSize: "1rem",
                fontWeight: "medium",
                color: "#000000",
                mb: 1,
              }}
            >
              First identification item
            </Typography>
            <FormControl
              sx={{
                maxWidth: "555px",
                width: "100%",
                mb: 1.5,
                mr: 10,
              }}
            >
              <InputLabel>Type</InputLabel>
              <Select
                value={shareholder.firstIdentificationType}
                onChange={(e) => handleInputChange(index + 1, e)}
                label="Type"
                name="firstIdentificationType"
                error={!!errors[index + 1]?.firstIdentificationType}
                helperText={
                  errors[index + 1]?.firstIdentificationType
                    ? "This field is required"
                    : ""
                }
              >
                {getFilteredOptionsForOtherIndex(
                  index + 1,
                  "firstIdentificationType"
                ).map((consentType) => (
                  <MenuItem key={consentType} value={consentType}>
                    {consentType}
                  </MenuItem>
                ))}
              </Select>
              {errors[index + 1]?.firstIdentificationType && (
                <FormHelperText sx={{ color: "red" }}>
                  {errors[index + 1].firstIdentificationType
                    ? "This field is required"
                    : ""}
                </FormHelperText>
              )}
            </FormControl>
            <Typography
              variant="body1"
              sx={{
                fontSize: "1rem",
                fontWeight: "medium",
                color: "#2F2D3B",
                maxWidth: "745px",
                width: "100%",
              }}
            >
              Answer
            </Typography>
            <TextField
              label="Answer"
              name="firstIdentificationAnswer"
              value={
                formData.shareHolders[index + 1]["firstIdentificationAnswer"]
              }
              error={!!errors[index + 1]?.firstIdentificationAnswer}
              helperText={
                errors[index + 1]?.firstIdentificationAnswer
                  ? "This field is required"
                  : ""
              }
              onChange={(e) => handleInputChange(index + 1, e)}
              sx={{
                maxWidth: "555px",
                width: "100%",
                mb: "1.2rem",
                mt: "0.8rem",
              }}
            />
            <Typography
              variant="body1"
              sx={{
                fontSize: "1rem",
                fontWeight: "medium",
                color: "#000000",
                mb: 1,
              }}
            >
              Second identification item
            </Typography>
            <FormControl
              sx={{
                maxWidth: "555px",
                width: "100%",
                mb: 1.5,
                mr: 10,
              }}
            >
              <InputLabel>Type</InputLabel>
              <Select
                value={shareholder.secondIdentificationType}
                onChange={(e) => handleInputChange(index + 1, e)}
                label="Type"
                error={!!errors[index + 1]?.secondIdentificationType}
                helperText={
                  errors[index + 1]?.secondIdentificationType
                    ? "This field is required"
                    : ""
                }
                name="secondIdentificationType"
              >
                {getFilteredOptionsForOtherIndex(
                  index + 1,
                  "secondIdentificationType"
                ).map((consentType) => (
                  <MenuItem key={consentType} value={consentType}>
                    {consentType}
                  </MenuItem>
                ))}
              </Select>
              {errors[index + 1]?.secondIdentificationType && (
                <FormHelperText sx={{ color: "red" }}>
                  {errors[index + 1].secondIdentificationType
                    ? "This field is required"
                    : ""}
                </FormHelperText>
              )}
            </FormControl>
            <Typography
              variant="body1"
              sx={{
                fontSize: "1rem",
                fontWeight: "medium",
                color: "#2F2D3B",
                maxWidth: "745px",
                width: "100%",
              }}
            >
              Answer
            </Typography>
            <TextField
              label="Answer"
              name="secondIdentificationAnswer"
              value={
                formData.shareHolders[index + 1]["secondIdentificationAnswer"]
              }
              onChange={(e) => handleInputChange(index + 1, e)}
              error={!!errors[index + 1]?.secondIdentificationAnswer}
              helperText={
                errors[index + 1]?.secondIdentificationAnswer
                  ? "This field is required"
                  : ""
              }
              sx={{
                maxWidth: "555px",
                width: "100%",
                mb: "1.2rem",
                mt: "0.8rem",
              }}
            />
            <Typography
              variant="body1"
              sx={{
                fontSize: "1rem",
                fontWeight: "medium",
                color: "#000000",
                mb: 1,
              }}
            >
              Third identification item
            </Typography>
            <FormControl
              sx={{
                maxWidth: "555px",
                width: "100%",
                mb: 1.5,
                mr: 10,
              }}
            >
              <InputLabel>Type</InputLabel>
              <Select
                value={shareholder.thirdIdentificationType}
                onChange={(e) => handleInputChange(index + 1, e)}
                label="Type"
                name="thirdIdentificationType"
                error={!!errors[index + 1]?.thirdIdentificationType}
                helperText={
                  errors[index + 1]?.thirdIdentificationType
                    ? "This field is required"
                    : ""
                }
              >
                {getFilteredOptionsForOtherIndex(
                  index + 1,
                  "thirdIdentificationType"
                ).map((consentType) => (
                  <MenuItem key={consentType} value={consentType}>
                    {consentType}
                  </MenuItem>
                ))}
              </Select>
              {errors[index + 1]?.thirdIdentificationType && (
                <FormHelperText sx={{ color: "red" }}>
                  {errors[index + 1].thirdIdentificationType
                    ? "This field is required"
                    : ""}
                </FormHelperText>
              )}
            </FormControl>
            <Typography
              variant="body1"
              sx={{
                fontSize: "1rem",
                fontWeight: "medium",
                color: "#2F2D3B",
                maxWidth: "745px",
                width: "100%",
              }}
            >
              Answer
            </Typography>
            <TextField
              label="Answer"
              name="thirdIdentificationAnswer"
              value={
                formData.shareHolders[index + 1]["thirdIdentificationAnswer"]
              }
              onChange={(e) => handleInputChange(index + 1, e)}
              error={!!errors[index + 1]?.thirdIdentificationAnswer}
              helperText={
                errors[index + 1]?.thirdIdentificationAnswer
                  ? "This field is required"
                  : ""
              }
              sx={{
                maxWidth: "555px",
                width: "100%",
                mb: "1.2rem",
                mt: "0.8rem",
              }}
            />
          </Box>
        );
      })}
      {formData.shareHolders.length < 4 && (
        <Typography
          variant="body1"
          sx={{
            fontSize: "1.1rem",
            fontWeight: "bold",
            color: "#2F2D3B",
            maxWidth: "745px",
            width: "100%",
            mt: 2,
            mb: 1,
          }}
        >
          Need to add other Directors or Shareholders?​
        </Typography>
      )}
      <Box
        sx={{
          display: "flex",
          maxWidth: "550px",
          width: "100%",
          mt: 2,
          justifyContent: "space-between",
        }}
      >
        {formData.shareHolders.length < 4 && (
          <Typography
            variant="body1"
            sx={{
              fontSize: "0.9rem",
              fontWeight: "medium",
              color: "#2069F7",
              cursor: "pointer",
            }}
            onClick={() => handleAddDirector()}
          >
            + Add Company Member
          </Typography>
        )}
        {formData.shareHolders.length > 1 && (
          <Typography
            variant="body1"
            sx={{
              fontSize: "0.9rem",
              fontWeight: "medium",
              color: "#2069F7",
              cursor: "pointer",
            }}
            onClick={() => handleRemoveDirector()}
          >
            - Remove Company Member
          </Typography>
        )}
      </Box>

      <Box mt={3} display="flex">
        <Button
          sx={{
            marginTop: "1rem",
            textTransform: "none",
            backgroundColor: "#ffffff",
            color: "#cfcfcf",
            border: "1px solid #cfcfcf",
            borderRadius: "1rem",
            padding: "0.4rem 1.5rem",
            fontWeight: 600,
            "&:hover": {
              color: "#2069F8",
              border: "1px solid #2069F8",
            },
          }}
          onClick={onBack}
        >
          Previous
        </Button>
        <Button
          sx={{
            marginTop: "1rem",
            marginLeft: "0.5rem",
            borderRadius: "1rem",
            backgroundColor: "#2069F8",
            color: "#ffffff",
            fontWeight: 700,
            padding: "0.5rem 1.5rem",
            textTransform: "none",
            width: "max-content",
            "&:hover": {
              backgroundColor: "#2069F8",
            },
          }}
          onClick={handleSubmit}
        >
          Continue
        </Button>
      </Box>
    </Box>
  );
};

export default Directors;
